// #6758 — How many rows a table shows at once, remembered for the reader.
//
// Modelled on useColumnVisibility: initialise to the table's default, rehydrate
// once from userSettings.performance.rows_per_page[key], write through on every
// change. Somebody who works through search terms a hundred at a time should not
// have to say so on every visit.
//
// Returns plain data and a callback, never a renderable — a hook that returns a
// component remounts it on every parent render.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { userSettingsQueryOptions, useUpdateUserSettingsMutation } from '@/lib/query-options';

export function useRowsPerPage({
  settingsKey,
  options,
  defaultRows,
}: {
  /** Key under userSettings.performance.rows_per_page (e.g. 'search_terms'). */
  settingsKey: string;
  /** The sizes offered. A saved value outside this list is ignored. */
  options: number[];
  defaultRows: number;
}): { rows: number; setRows: (rows: number) => void } {
  const { data: userSettings } = useQuery(userSettingsQueryOptions());
  const settingsMutation = useUpdateUserSettingsMutation();

  const [rows, setRowsState] = useState(defaultRows);

  // Once only: a later write of our own must not bounce back and overwrite a
  // choice the reader has since made.
  const loaded = useRef(false);
  useEffect(() => {
    if (!userSettings || loaded.current) return;
    loaded.current = true;
    const saved = userSettings.performance?.rows_per_page?.[settingsKey];
    // A size no longer offered is not honoured: the list can change between
    // releases, and a stale 250 would page a table nobody meant to page.
    if (typeof saved === 'number' && options.includes(saved)) setRowsState(saved);
  }, [userSettings, settingsKey, options]);

  const setRows = useCallback(
    (next: number) => {
      setRowsState(next);
      settingsMutation.mutate({
        ...userSettings,
        performance: {
          ...userSettings?.performance,
          rows_per_page: { ...userSettings?.performance?.rows_per_page, [settingsKey]: next },
        },
      });
    },
    [settingsKey, settingsMutation, userSettings],
  );

  return { rows, setRows };
}
